import { z } from 'zod';
import { activityOperationSchema, workspaceContextSchema } from './workspace-runtime.js';
const absolutePath = z
    .string()
    .min(1)
    .max(512)
    .startsWith('/')
    .refine((value) => !value.includes('\0'));
export const editorSessionRequestSchema = workspaceContextSchema
    .extend({ sessionId: z.uuid(), replaceSessionId: z.uuid().optional() })
    .strict();
export const editorSessionDescriptorSchema = z
    .object({
    sessionId: z.uuid(),
    context: workspaceContextSchema,
    token: z.string().regex(/^[a-f0-9]{64}$/),
    socketPath: absolutePath,
    activityRoot: absolutePath,
    workspaceFile: absolutePath,
})
    .strict();
export const editorSessionSchema = z
    .object({ sessionId: z.uuid(), workspaceFile: absolutePath })
    .strict();
export const editorStatusSchema = z
    .object({
    sessionId: z.uuid().nullable(),
    connected: z.boolean(),
    // Older runtimes omit this; liveness remains separate from folder readiness.
    workspaceReady: z.boolean().optional(),
    // True once the runtime has proven no browser has talked to the editor for
    // two minutes, even if a retained extension host still polls. Omitted by
    // older runtimes and by probes that did not ask for it.
    browserAbsent: z.boolean().optional(),
    lastUserActivityAt: z.iso.datetime().nullable().default(null),
    workspaceFile: absolutePath.nullable(),
})
    .strict();
export const editorConnectionSchema = z
    .object({
    connected: z.boolean(),
    workspaceReady: z.boolean().optional(),
    browserAbsent: z.boolean().optional(),
})
    .strict();
export const workspaceLaunchRequestSchema = z
    .object({
    sessionId: z.uuid(),
    expectedGeneration: z.number().int().positive(),
    expectedAttemptId: z.uuid(),
    recoverDisconnected: z.boolean().optional(),
})
    .strict();
export const workspacePreviewRequestSchema = workspaceLaunchRequestSchema.pick({
    expectedGeneration: true,
    expectedAttemptId: true,
});
export const gatewayContextSchema = z
    .object({
    authorizationId: z.uuid().optional(),
    coderId: z.uuid(),
})
    .strict();
export const editorCaptureRequestSchema = activityOperationSchema
    .extend({ sessionId: z.uuid() })
    .strict();
export const editorCommandSchema = z
    .object({
    id: z.uuid(),
    operationId: z.uuid(),
    action: z.enum(['save', 'verify', 'verify-current']),
    context: workspaceContextSchema,
})
    .strict();
export const editorErrorSchema = z.enum([
    'editor_untitled',
    'editor_outside_activity',
    'editor_save_failed',
    'editor_changed',
    'editor_context_changed',
    'editor_too_many_files',
    'editor_unsupported_document',
    'editor_capture_interrupted',
    'editor_disconnected',
]);
export const editorSessionErrorSchema = z.enum(['editor_in_use', 'editor_session_changed']);
/**
 * Bridge limits the learner can act on: the activity as captured is too large,
 * too deep, unsafe or changed while saving. They repeat identically on a retry,
 * so they end the command and ask for a corrected save instead of a provider retry.
 */
export const captureLimitErrorSchema = z.enum([
    'project_too_large',
    'project_file_too_large',
    'project_too_many_files',
    'project_too_many_entries',
    'project_too_many_exclusions',
    'project_too_deep',
    'project_path_too_long',
    'unsafe_project_path',
    'unsafe_project_root',
    'unsafe_path',
    'unsupported_source_encoding',
    'workspace_files_changed',
]);
export const editorResultSchema = z
    .object({
    id: z.uuid(),
    operationId: z.uuid(),
    errorCode: editorErrorSchema.nullable(),
})
    .strict();
export const editorPollSchema = z.object({ command: editorCommandSchema.nullable() }).strict();
